import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App,
  Button,
  Drawer,
  Flex,
  Form,
  Input,
  Segmented,
  Select,
  Spin,
  Typography
} from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChecklistItem } from 'src/analytics'
import {
  organizationsRolesCreateMutation,
  organizationsRolesListOptions,
  organizationsRolesListQueryKey,
  organizationsRolesPartialUpdateMutation,
  type OrganizationRoleFull
} from 'src/api/main'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { useInvalidatePredicateQueries } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

const { Text } = Typography

type FormValues = {
  name: string
  description?: string
}

type Mode = 'create' | 'workspace'

type Props = {
  open: boolean
  role: OrganizationRoleFull | null
  applicationId?: number
  organizationId: number
  onClose: () => void
}

const WORKSPACE_ROLES_PAGE_SIZE = 200

const slugifyName = (name: string) =>
  name.toLowerCase().trim().replace(/\s+/g, '_')

const buildClonedCodename = (
  src: OrganizationRoleFull,
  applicationId?: number
) => {
  if (src.codename) {
    return `${src.codename}_${slugifyName(`app${applicationId ?? ''}`)}`
  }

  return slugifyName(src.name)
}

const useRoleCreateMutations = (onSuccess: (notice: string) => void) => {
  const { t } = useTranslation()
  const showApiErrors = useShowApiErrors()
  const reportStep = useReportJourneyStep()

  const { mutateAsync: createAsync, isPending: isCreating } = useMutation({
    ...organizationsRolesCreateMutation(),
    onSuccess: () => reportStep(ChecklistItem.ROLES)
  })

  const { mutate: updateRole, isPending: isUpdating } = useMutation({
    ...organizationsRolesPartialUpdateMutation(),
    onSuccess: () => {
      reportStep(ChecklistItem.ROLES)
      onSuccess(t('workspace.apps.roles.roleUpdated'))
    },
    onError: (error) => showApiErrors(error)
  })

  return {
    createAsync,
    updateRole,
    isBusy: isCreating || isUpdating
  }
}

const CreateRoleForm = ({
  role,
  applicationId,
  organizationId,
  isBusy,
  createAsync,
  updateRole,
  onSuccess,
  onClose
}: {
  role: OrganizationRoleFull | null
  applicationId?: number
  organizationId: number
  isBusy: boolean
  createAsync: ReturnType<typeof useRoleCreateMutations>['createAsync']
  updateRole: ReturnType<typeof useRoleCreateMutations>['updateRole']
  onSuccess: (notice: string) => void
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const showApiErrors = useShowApiErrors()
  const [form] = Form.useForm<FormValues>()
  const isEdit = Boolean(role)

  const initialValues = useMemo<FormValues>(
    () =>
      isEdit && role
        ? { name: role.name, description: role.description ?? '' }
        : { name: '', description: '' },
    [isEdit, role]
  )

  const handleFinish = (values: FormValues) => {
    if (isEdit && role) {
      updateRole({
        path: { id: role.id },
        body: {
          name: values.name,
          description: values.description ?? '',
          permissions_ids: role.permissions?.map((p) => p.id) ?? []
        }
      })
      return
    }
    createAsync({
      body: {
        name: values.name,
        codename: slugifyName(values.name),
        description: values.description ?? '',
        organization: organizationId,
        permissions_ids: [],
        application: applicationId
      }
    })
      .then(() => onSuccess(t('workspace.apps.roles.roleCreated')))
      .catch((error) => showApiErrors(error))
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={initialValues}
        onFinish={handleFinish}
      >
        <Form.Item
          label={t('name')}
          name="name"
          rules={[{ required: true, message: t('required_field_error') }]}
        >
          <Input placeholder={t('workspace.apps.roles.roleNamePlaceholder')} />
        </Form.Item>
        <Form.Item
          label={t('workspace.apps.roles.descriptionLabel')}
          name="description"
        >
          <Input.TextArea
            rows={3}
            placeholder={t('workspace.apps.roles.roleDescriptionPlaceholder')}
          />
        </Form.Item>
      </Form>

      <Flex justify="end" gap={8}>
        <Button onClick={onClose} disabled={isBusy}>
          {t('cancel')}
        </Button>
        <Button type="primary" loading={isBusy} onClick={() => form.submit()}>
          {isEdit ? t('save') : t('workspace.apps.roles.add')}
        </Button>
      </Flex>
    </>
  )
}

const CloneFromWorkspaceForm = ({
  applicationId,
  organizationId,
  isBusy,
  createAsync,
  onSuccess,
  onClose
}: {
  applicationId?: number
  organizationId: number
  isBusy: boolean
  createAsync: ReturnType<typeof useRoleCreateMutations>['createAsync']
  onSuccess: (notice: string) => void
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const showApiErrors = useShowApiErrors()
  const [picked, setPicked] = useState<number[]>([])

  const { data, isLoading } = useQuery({
    ...organizationsRolesListOptions({
      query: {
        organization: organizationId,
        per_page: WORKSPACE_ROLES_PAGE_SIZE
      }
    })
  })

  const workspaceRoles = useMemo(
    () => (data?.results ?? []).filter((r) => (r.application ?? null) == null),
    [data]
  )

  const handleClone = async () => {
    if (picked.length === 0) return
    try {
      await Promise.all(
        picked.map((roleId) => {
          const src = workspaceRoles.find((r) => r.id === roleId)
          if (!src) return Promise.resolve()
          return createAsync({
            body: {
              name: src.name,
              codename: buildClonedCodename(src, applicationId),
              description: src.description ?? '',
              organization: organizationId,
              permissions_ids: [],
              application: applicationId
            }
          })
        })
      )
      onSuccess(t('workspace.apps.roles.roleCreated'))
    } catch (error) {
      showApiErrors(error)
    }
  }

  if (isLoading) {
    return (
      <Flex justify="center" style={{ padding: 24 }}>
        <Spin />
      </Flex>
    )
  }

  return (
    <>
      <Form layout="vertical" requiredMark={false}>
        <Form.Item
          label={t('workspace.apps.roles.workspaceRolesLabel', {
            defaultValue: 'Workspace roles'
          })}
        >
          <Select<number[]>
            mode="multiple"
            showSearch={{ optionFilterProp: 'label' }}
            placeholder={t('workspace.apps.roles.workspaceRolesPlaceholder', {
              defaultValue: 'Pick workspace roles to clone…'
            })}
            value={picked}
            onChange={setPicked}
            options={workspaceRoles.map((r) => ({
              value: r.id,
              label: r.name
            }))}
          />
        </Form.Item>
      </Form>

      <Flex justify="end" gap={8}>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button
          type="primary"
          loading={isBusy}
          disabled={picked.length === 0}
          onClick={() => void handleClone()}
        >
          {t('workspace.apps.roles.addFromWorkspace', {
            defaultValue:
              `Add ${picked.length || ''} role${picked.length === 1 ? '' : 's'}`.trim()
          })}
        </Button>
      </Flex>
    </>
  )
}

const ModePicker = ({
  mode,
  onChange
}: {
  mode: Mode
  onChange: (mode: Mode) => void
}) => {
  const { t } = useTranslation()

  return (
    <Flex vertical gap={12}>
      <Segmented<Mode>
        block
        value={mode}
        onChange={onChange}
        options={[
          {
            label: t('workspace.apps.roles.modeCreate', {
              defaultValue: 'Create new'
            }),
            value: 'create'
          },
          {
            label: t('workspace.apps.roles.modeFromWorkspace', {
              defaultValue: 'From workspace'
            }),
            value: 'workspace'
          }
        ]}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {mode === 'create'
          ? t('workspace.apps.roles.modeCreateHint', {
              defaultValue:
                'Create a brand-new role scoped to this app. Permissions are filled in the matrix below.'
            })
          : t('workspace.apps.roles.modeFromWorkspaceHint', {
              defaultValue:
                'Clone one or more existing workspace roles into this app. Each clone starts empty; assign app permissions afterwards.'
            })}
      </Text>
    </Flex>
  )
}

const RoleEditorBody = ({
  role,
  applicationId,
  organizationId,
  onClose
}: Omit<Props, 'open'>) => {
  const { message } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const [mode, setMode] = useState<Mode>('create')
  const isEdit = Boolean(role)

  const [isFinishing, setIsFinishing] = useState(false)

  const onSuccess = async (notice: string) => {
    setIsFinishing(true)
    await invalidateQueries([organizationsRolesListQueryKey()])
    message.success(notice)
    onClose()
    setIsFinishing(false)
  }

  const { createAsync, updateRole, isBusy } = useRoleCreateMutations(onSuccess)
  const busy = isBusy || isFinishing

  return (
    <Flex vertical gap={16}>
      {!isEdit ? <ModePicker mode={mode} onChange={setMode} /> : null}

      {!isEdit && mode === 'workspace' ? (
        <CloneFromWorkspaceForm
          applicationId={applicationId}
          organizationId={organizationId}
          isBusy={busy}
          createAsync={createAsync}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      ) : (
        <CreateRoleForm
          role={role}
          applicationId={applicationId}
          organizationId={organizationId}
          isBusy={busy}
          createAsync={createAsync}
          updateRole={updateRole}
          onSuccess={onSuccess}
          onClose={onClose}
        />
      )}
    </Flex>
  )
}

export const RoleEditorDrawer = ({ open, ...rest }: Props) => {
  const { t } = useTranslation()
  const isEditMode = Boolean(rest.role)

  return (
    <Drawer
      title={
        isEditMode
          ? t('workspace.apps.roles.editModalTitle')
          : t('workspace.apps.roles.modalTitle')
      }
      open={open}
      size={460}
      destroyOnHidden
      onClose={rest.onClose}
    >
      {open ? <RoleEditorBody {...rest} /> : null}
    </Drawer>
  )
}
